import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import BloggerRichEditor from "@/components/BloggerRichEditor";
import TransliterateInput from "@/components/TransliterateInput";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import {
  Sparkle, FloppyDisk, PaperPlaneTilt, Clock, Eye, X, Cloud, CloudArrowUp,
  DeviceMobile, DeviceTablet, Desktop, LinkSimple, CaretDown, CaretUp,
  ArrowLeft, Copy, ArrowsClockwise, ArrowsOut, DownloadSimple,
  Tag, Compass, MapPin, GearSix, MagnifyingGlass,
} from "@phosphor-icons/react";

const AI_ACTIONS = [
  { intent: "improve-title", label: "Improve title", target: "title" },
  { intent: "create-subtitle", label: "Create subtitle", target: "subtitle" },
  { intent: "create-excerpt", label: "Create excerpt", target: "excerpt" },
  { intent: "suggest-tags", label: "Suggest tags", target: "tags" },
  { intent: "suggest-category", label: "Suggest category", target: "category" },
  { intent: "improve-grammar", label: "Fix grammar", target: "body" },
  { intent: "shorten", label: "Shorten", target: "body" },
  { intent: "rewrite", label: "Rewrite", target: "body" },
  { intent: "seo-description", label: "SEO description", target: "seo_description" },
  { intent: "share-text", label: "Share caption", target: "share_text" },
];
const NETWORK_SITES = [
  { id: "naturepulse", label: "NaturePulse" },
  { id: "traveler-pulse", label: "TravelerPulse" },
  { id: "food-pulse", label: "FoodPulse" },
  { id: "kerala-com", label: "Kerala.com" },
  { id: "indias-com", label: "Indias.com" },
  { id: "kerala-health", label: "KeralaHealth" },
  { id: "alumni-pulse", label: "AlumniPulse" },
  { id: "photo-pulse", label: "PhotoPulse" },
];

function slugify(t) {
  return (t || "").toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").slice(0, 80);
}

/**
 * Collapsible sidebar section — mimics Blogger's post-settings panels.
 */
const SidebarSection = ({ icon: Icon, label, initialOpen = false, testid, children }) => {
  const [open, setOpen] = useState(initialOpen);
  return (
    <div className="border-t border-black/10 first:border-t-0" data-testid={testid}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 py-3 px-3 hover:bg-black/5">
        <Icon size={16} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
        <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#241209] flex-1 text-left">{label}</span>
        {open ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />}
      </button>
      {open && <div className="px-3 pb-4 pt-1 space-y-2">{children}</div>}
    </div>
  );
};

export default function PublisherEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [post, setPost] = useState({
    id: null,
    story_type: "personal_story",
    title: "", subtitle: "", excerpt: "",
    body_html: "", slug: "",
    category: "", tags: [], species: "",
    featured_image_url: "",
    seo_title: "", seo_description: "",
    language: "en",
    co_authors: [],
    network_distribution: ["naturepulse"],
    site_id: "naturepulse",
    visibility: "public",
    comments_enabled: true,
    moderation_status: "draft",
    short_url: null, qr_url: null,
    scheduled_at: null,
  });
  const [categories, setCategories] = useState([]);
  const [aiBusy, setAiBusy] = useState(null);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [publishing, setPublishing] = useState(false);
  const [previewMode, setPreviewMode] = useState("desktop");
  const [previewRotated, setPreviewRotated] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [linkExpanding, setLinkExpanding] = useState(false);
  const [expandUrl, setExpandUrl] = useState("");
  const dirtyRef = useRef(false);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    http.get("/publisher/categories").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!id || loading) return;
    http.get(`/publisher/posts/${id}`)
      .then(({ data }) => setPost((s) => ({ ...s, ...data.post })))
      .catch(() => toast.error("Could not load post"));
  }, [id, loading]);
  useEffect(() => {
    if (loading) return;
    if (!user) nav("/");
  }, [user, loading, nav]);

  // Autosave debounced 4s after last change.
  useEffect(() => {
    if (!dirtyRef.current) return;
    if (!post.title && !post.body_html) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => { void saveDraft({ silent: true }); }, 4000);
    return () => saveTimerRef.current && clearTimeout(saveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.title, post.subtitle, post.body_html, post.excerpt, post.category, post.tags,
      post.species, post.featured_image_url, post.slug, post.seo_title, post.seo_description,
      post.network_distribution, post.co_authors]);

  const setField = (patch) => {
    dirtyRef.current = true;
    setPost((s) => ({ ...s, ...patch }));
  };

  const buildPayload = () => ({
    story_type: post.story_type,
    title: post.title,
    subtitle: post.subtitle,
    excerpt: post.excerpt,
    body_html: post.body_html,
    slug: post.slug || slugify(post.title),
    category: post.category || undefined,
    tags: post.tags,
    species: post.species || undefined,
    featured_image_url: post.featured_image_url || undefined,
    seo_title: post.seo_title || undefined,
    seo_description: post.seo_description || undefined,
    language: post.language,
    co_authors: (post.co_authors || []).map((c) => c.user_id || c),
    network_distribution: post.network_distribution,
    site_id: post.site_id,
    visibility: post.visibility,
    comments_enabled: post.comments_enabled,
  });

  const saveDraft = async ({ silent } = {}) => {
    if (saveState === "saving") return null;
    // Server rejects "empty" posts — mirror that guard here so autosave
    // doesn't spam toasts for a brand-new blank editor.
    const isEmpty = !(post.title || "").trim() && ((post.body_html || "").replace(/<[^>]+>/g, "").trim().length < 10);
    if (isEmpty) return null;
    setSaveState("saving");
    try {
      const payload = buildPayload();
      const url = post.id ? `/publisher/posts/${post.id}` : "/publisher/posts";
      const method = post.id ? "patch" : "post";
      const { data } = await http[method](url, payload);
      const savedPost = data.post || {};
      setPost((s) => ({ ...s, ...savedPost }));
      dirtyRef.current = false;
      setSaveState("saved");
      if (!silent) toast.success("Draft saved");
      if (!post.id && savedPost.id) {
        window.history.replaceState({}, "", `/publisher/edit/${savedPost.id}`);
      }
      return savedPost;
    } catch (e) {
      setSaveState("error");
      if (!silent) toast.error(e?.response?.data?.detail || "Save failed");
      return null;
    }
  };

  const publish = async () => {
    if (!post.title || !post.body_html) return toast.error("Title and body are required to publish.");
    setPublishing(true);
    try {
      const saved = await saveDraft({ silent: true });
      const currentId = saved?.id || post.id;
      if (!currentId) throw new Error("Could not save draft before publishing.");
      const { data } = await http.post(`/publisher/posts/${currentId}/publish`);
      setPost((s) => ({ ...s, ...saved, moderation_status: data.status, short_url: data.short_url || s.short_url, qr_url: data.qr_url || s.qr_url }));
      toast.success(data.status === "approved" ? "Published!" : "Sent for review");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Publish failed"); }
    finally { setPublishing(false); }
  };

  const schedule = async () => {
    if (!scheduledAt) return toast.error("Pick a date & time");
    try {
      const saved = await saveDraft({ silent: true });
      const currentId = saved?.id || post.id;
      if (!currentId) throw new Error("Could not save draft before scheduling.");
      const dt = new Date(scheduledAt).toISOString();
      await http.post(`/publisher/posts/${currentId}/schedule`, { scheduled_at: dt });
      setPost((s) => ({ ...s, moderation_status: "scheduled", scheduled_at: dt }));
      toast.success("Scheduled");
      setShowSchedule(false);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Schedule failed"); }
  };

  const runAI = async (intent, target) => {
    if (!post.body_html && intent !== "suggest-tags") return toast.error("Write some body first.");
    setAiBusy(intent);
    try {
      const { data } = await http.post("/publisher/ai/assist", {
        intent, title: post.title, body: post.body_html || post.excerpt || post.title,
        language: post.language || "en",
      });
      const r = data.result || {};
      if (target === "title" && r.title) setField({ title: r.title });
      else if (target === "subtitle" && r.subtitle) setField({ subtitle: r.subtitle });
      else if (target === "excerpt" && r.excerpt) setField({ excerpt: r.excerpt });
      else if (target === "tags" && Array.isArray(r.tags)) setField({ tags: Array.from(new Set([...(post.tags || []), ...r.tags])).slice(0, 20) });
      else if (target === "category" && r.category) setField({ category: r.category });
      else if (target === "seo_description" && r.seo_description) setField({ seo_description: r.seo_description });
      else if (target === "body" && r.body) setField({ body_html: `<p>${r.body.replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br/>")}</p>` });
      else if (target === "share_text" && r.share_text) navigator.clipboard.writeText(r.share_text).then(() => toast.success("Share text copied"));
      else toast.info("AI returned an empty response — try again.");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI unavailable"); }
    finally { setAiBusy(null); }
  };

  const expandLink = async () => {
    if (!expandUrl.trim()) return;
    setLinkExpanding(true);
    try {
      const { data } = await http.post("/nature/stories/analyze", { url: expandUrl.trim() });
      const a = data.article || {};
      const cardHtml = (
        `<div class="link-card" data-source="${a.source_domain || ""}">` +
        `<a href="${a.url || expandUrl}" target="_blank" rel="noreferrer noopener">${a.headline || "External link"}</a>` +
        (a.summary ? `<p>${a.summary}</p>` : "") +
        (a.publisher ? `<small>Source: ${a.publisher}</small>` : "") +
        `</div><p></p>`
      );
      setField({ body_html: (post.body_html || "") + cardHtml });
      if (a.headline && !post.title) setField({ title: a.headline });
      if (a.summary && !post.excerpt) setField({ excerpt: a.summary });
      if (Array.isArray(a.tags)) setField({ tags: Array.from(new Set([...(post.tags || []), ...a.tags])).slice(0, 20) });
      toast.success("Link expanded");
      setExpandUrl("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not expand link"); }
    finally { setLinkExpanding(false); }
  };

  const addTag = (t) => {
    const cleaned = (t || "").trim().toLowerCase();
    if (!cleaned) return;
    if (post.tags.includes(cleaned)) return;
    setField({ tags: [...post.tags, cleaned].slice(0, 20) });
  };
  const removeTag = (t) => setField({ tags: post.tags.filter((x) => x !== t) });

  // Escape-key + backdrop dismissal for modals.
  useEffect(() => {
    if (!showPreview && !showSchedule) return;
    const onKey = (e) => { if (e.key === "Escape") { setShowPreview(false); setShowSchedule(false); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showPreview, showSchedule]);

  const openPreviewInNewTab = useCallback(() => {
    const html = `<!doctype html><html><head><meta charset="utf-8"/><title>${(post.title || "Preview").replace(/</g, "&lt;")}</title>` +
      `<style>body{font-family:Georgia,serif;max-width:720px;margin:2rem auto;padding:0 1rem;background:#F5EBDC;color:#151312}h1{font-size:2rem;line-height:1.1}img{max-width:100%;border-radius:.5rem}article{margin-top:1rem}</style>` +
      `</head><body>${post.featured_image_url ? `<img src="${post.featured_image_url}"/>` : ""}` +
      `<h1>${(post.title || "Untitled").replace(/</g, "&lt;")}</h1>` +
      (post.subtitle ? `<p><em>${post.subtitle.replace(/</g, "&lt;")}</em></p>` : "") +
      `<article>${post.body_html || "<p><em>Empty draft.</em></p>"}</article></body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.open(); w.document.write(html); w.document.close(); }
  }, [post]);

  const previewSize = useMemo(() => {
    const base = { desktop: [960, 640], tablet: [768, 1024], mobile: [380, 720] }[previewMode];
    return previewRotated && previewMode !== "desktop" ? [base[1], base[0]] : base;
  }, [previewMode, previewRotated]);

  if (loading || !user) return <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5"><div className="max-w-[900px] mx-auto animate-pulse h-64 bg-white/60 rounded-2xl" /></main>;

  const SaveIcon = saveState === "saving" ? CloudArrowUp : Cloud;
  const saveLabel = ({
    saving: "Saving…", saved: "Saved", error: "Save error", idle: post.id ? "Draft loaded" : "Draft",
  })[saveState];

  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-16 pb-32" data-testid="publisher-editor">
      {/* Sticky top action bar — Blogger-style */}
      <div className="sticky top-16 z-30 border-b border-black/10 bg-white">
        <div className="max-w-[1400px] mx-auto flex items-center gap-2 px-4 py-2.5 flex-wrap">
          <button onClick={() => nav("/publisher")} data-testid="editor-back" title="Back to posts" className="rounded-full h-8 w-8 grid place-items-center hover:bg-black/5"><ArrowLeft size={16} weight="bold" /></button>
          <div className="min-w-0">
            <div className="font-mono-tech text-[9px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">/ Publisher</div>
            <div className="text-xs text-[#3A3830] truncate max-w-[280px]">{post.title || <span className="italic text-[#3A3830]">Untitled</span>}</div>
          </div>
          <div className="flex-1" />
          <div className="inline-flex items-center gap-1 text-[10px] text-[#3A3830] font-mono-tech tracking-[0.24em] uppercase" data-testid="editor-save-state">
            <SaveIcon size={13} weight={saveState === "saved" ? "fill" : "regular"} className={saveState === "saved" ? "text-emerald-600" : saveState === "error" ? "text-red-600" : ""} />
            {saveLabel}
            {post.moderation_status && post.moderation_status !== "draft" && (
              <span className="ml-1 text-[color:var(--np-brand,#B85F2A)]">· {post.moderation_status}</span>
            )}
          </div>
          <button onClick={() => saveDraft({})} data-testid="editor-save" title="Save draft" className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-black/5">
            <FloppyDisk size={12} weight="fill" /> Save
          </button>
          <button onClick={() => setShowPreview(true)} data-testid="editor-preview" className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-black/5">
            <Eye size={12} weight="fill" /> Preview
          </button>
          <button onClick={() => setShowSchedule(true)} data-testid="editor-schedule" className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-black/5">
            <Clock size={12} weight="fill" /> Schedule
          </button>
          <button onClick={publish} disabled={publishing} data-testid="editor-publish" className="inline-flex items-center gap-2 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-5 py-2 font-mono-tech text-[10px] tracking-[0.32em] uppercase font-semibold hover:opacity-90 disabled:opacity-50 shadow">
            <PaperPlaneTilt size={12} weight="fill" /> {publishing ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 mt-6">
        {/* MAIN column */}
        <div className="space-y-3">
          <TransliterateInput
            as="input"
            value={post.title}
            onChange={(e) => setField({ title: e.target.value })}
            placeholder="Title"
            testid="editor-title"
            className="!text-4xl md:!text-5xl !font-serif-display !py-3 !border-transparent focus:!border-b focus:!border-[color:var(--np-brand,#B85F2A)] !rounded-none !bg-[#F5EBDC]"
          />
          <TransliterateInput
            as="input"
            value={post.subtitle || ""}
            onChange={(e) => setField({ subtitle: e.target.value })}
            placeholder="Subtitle (optional)"
            testid="editor-subtitle"
            className="!text-lg !text-[#3A3830] !italic !border-transparent !bg-[#F5EBDC]"
          />

          {/* Link expansion strip */}
          <div className="flex items-center gap-2 rounded-full bg-white border border-black/10 px-3 py-1.5">
            <LinkSimple size={12} weight="bold" className="text-[color:var(--np-brand,#B85F2A)]" />
            <input
              value={expandUrl}
              onChange={(e) => setExpandUrl(e.target.value)}
              placeholder="Paste a link to auto-fill title, excerpt & tags…"
              data-testid="editor-expand-url"
              className="flex-1 bg-transparent outline-none text-xs"
            />
            <button onClick={expandLink} disabled={linkExpanding} data-testid="editor-expand-btn" className="rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-3 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase disabled:opacity-50">
              {linkExpanding ? "…" : "Expand"}
            </button>
          </div>

          <BloggerRichEditor
            value={post.body_html}
            onChange={(html) => setField({ body_html: html })}
            placeholder="Write your story…"
            testid="editor-body"
            onTranslate={() => window.open(`https://translate.google.com/translate?sl=auto&tl=hi&u=${encodeURIComponent(window.location.origin + `/stories/${post.id || ""}`)}`, "_blank")}
          />
        </div>

        {/* SIDE column — Blogger-style collapsible settings */}
        <aside className="rounded-lg bg-white border border-black/10 h-fit overflow-hidden">
          {/* AI assist (always open at top) */}
          <div className="p-3">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2 flex items-center gap-1"><Sparkle size={11} weight="fill" /> AI assist</div>
            <div className="grid grid-cols-2 gap-1">
              {AI_ACTIONS.map((a) => (
                <button
                  key={a.intent}
                  onClick={() => runAI(a.intent, a.target)}
                  disabled={aiBusy === a.intent}
                  data-testid={`ai-${a.intent}`}
                  className="rounded-md border border-black/10 bg-[#FBF2E1]/60 px-2 py-1.5 font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#241209] hover:bg-[#FBF2E1] disabled:opacity-50"
                >
                  {aiBusy === a.intent ? "…" : a.label}
                </button>
              ))}
            </div>
          </div>

          <SidebarSection icon={Tag} label={`Labels · ${post.tags?.length || 0}`} initialOpen testid="side-labels">
            <div className="flex flex-wrap gap-1">
              {(post.tags || []).map((t) => (
                <span key={t} data-testid={`tag-chip-${t}`} className="inline-flex items-center gap-1 rounded-full bg-[#FBF2E1] border border-black/10 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">
                  {t}
                  <button onClick={() => removeTag(t)} title="Remove" data-testid={`tag-remove-${t}`} className="text-[#3A3830] hover:text-black"><X size={9} weight="bold" /></button>
                </span>
              ))}
            </div>
            <input
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
                  e.preventDefault();
                  addTag(e.currentTarget.value);
                  e.currentTarget.value = "";
                }
              }}
              placeholder="Separate labels by commas"
              data-testid="editor-tag-input"
              className="w-full bg-white border border-black/15 rounded-md px-2 py-1.5 text-xs"
            />
            <div className="text-[10px] text-[#3A3830] italic">No matching suggestions</div>
          </SidebarSection>

          <SidebarSection icon={Compass} label="Category" testid="side-category">
            <select
              value={post.category || ""}
              onChange={(e) => setField({ category: e.target.value })}
              data-testid="editor-category"
              className="w-full bg-white border border-black/15 rounded-md px-2 py-1.5 text-xs"
            >
              <option value="">—</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input
              value={post.species || ""}
              onChange={(e) => setField({ species: e.target.value })}
              placeholder="Species (optional, e.g. Monarch Butterfly)"
              data-testid="editor-species"
              className="w-full bg-white border border-black/15 rounded-md px-2 py-1.5 text-xs"
            />
          </SidebarSection>

          <SidebarSection icon={Clock} label={post.moderation_status === "scheduled" ? `Scheduled · ${new Date(post.scheduled_at).toLocaleString()}` : "Published on"} testid="side-schedule">
            <button onClick={() => setShowSchedule(true)} className="w-full rounded-md border border-black/15 bg-white px-2 py-1.5 text-xs hover:bg-black/5">
              Set date & time
            </button>
            <div className="text-[10px] text-[#3A3830]">Automatic — publishes now</div>
          </SidebarSection>

          <SidebarSection icon={LinkSimple} label="Permalink" testid="side-permalink">
            <input
              value={post.slug || ""}
              onChange={(e) => setField({ slug: e.target.value })}
              placeholder={slugify(post.title) || "auto-generated"}
              data-testid="editor-slug"
              className="w-full bg-white border border-black/15 rounded-md px-2 py-1.5 text-xs font-mono"
            />
            {post.short_url && (
              <div className="mt-2 space-y-1">
                <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#3A3830]">splink.pro</div>
                <div className="flex items-center gap-1">
                  <a href={post.short_url} target="_blank" rel="noreferrer noopener" data-testid="editor-short-url" className="flex-1 text-[color:var(--np-brand,#B85F2A)] text-xs underline truncate">{post.short_url}</a>
                  <button onClick={() => { navigator.clipboard.writeText(post.short_url); toast.success("Copied"); }} className="rounded-full border border-black/15 bg-white p-1" title="Copy"><Copy size={10} weight="bold" /></button>
                </div>
                {post.qr_url && (
                  <div className="mt-1 flex items-center gap-2">
                    <img src={post.qr_url} alt="QR" data-testid="editor-qr" className="w-16 h-16 border border-black/10 rounded" />
                    <a href={post.qr_url} download className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-2 py-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-black/5"><DownloadSimple size={9} weight="bold" /> Save QR</a>
                  </div>
                )}
              </div>
            )}
          </SidebarSection>

          <SidebarSection icon={MapPin} label="Location" testid="side-location">
            <div className="text-[10px] text-[#3A3830]">Set location on the story after publishing.</div>
          </SidebarSection>

          <SidebarSection icon={Compass} label="Distribute across sites" testid="side-distribution">
            <div className="flex flex-wrap gap-1">
              {NETWORK_SITES.map((s) => {
                const active = post.network_distribution?.includes(s.id);
                const isPrimary = s.id === post.site_id;
                return (
                  <button
                    key={s.id}
                    onClick={() => {
                      if (isPrimary) return;
                      const nd = active
                        ? post.network_distribution.filter((x) => x !== s.id)
                        : Array.from(new Set([...(post.network_distribution || []), s.id]));
                      setField({ network_distribution: nd });
                    }}
                    data-testid={`distribute-${s.id}`}
                    className={`rounded-full px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase border ${
                      active
                        ? "border-[color:var(--np-brand,#B85F2A)] bg-[color:var(--np-brand,#B85F2A)] text-white"
                        : "border-black/15 bg-white text-[#241209] hover:bg-black/5"
                    } ${isPrimary ? "cursor-default" : ""}`}
                  >
                    {s.label}{isPrimary ? " · primary" : ""}
                  </button>
                );
              })}
            </div>
          </SidebarSection>

          <SidebarSection icon={MagnifyingGlass} label="Search description (SEO)" testid="side-seo">
            <input value={post.seo_title || ""} onChange={(e) => setField({ seo_title: e.target.value })} placeholder="SEO title" data-testid="editor-seo-title" className="w-full bg-white border border-black/15 rounded-md px-2 py-1.5 text-xs" />
            <textarea value={post.seo_description || ""} onChange={(e) => setField({ seo_description: e.target.value })} placeholder="Meta description (140-160 chars)" data-testid="editor-seo-description" rows={2} className="w-full bg-white border border-black/15 rounded-md px-2 py-1.5 text-xs" />
          </SidebarSection>

          <SidebarSection icon={GearSix} label="Options" testid="side-options">
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={post.comments_enabled} onChange={(e) => setField({ comments_enabled: e.target.checked })} data-testid="editor-comments-enabled" />
              Allow reader comments
            </label>
            <select
              value={post.visibility}
              onChange={(e) => setField({ visibility: e.target.value })}
              data-testid="editor-visibility"
              className="w-full bg-white border border-black/15 rounded-md px-2 py-1.5 text-xs"
            >
              <option value="public">Public</option>
              <option value="unlisted">Unlisted (only with link)</option>
              <option value="private">Private (only me)</option>
            </select>
          </SidebarSection>

          <SidebarSection icon={GearSix} label="Featured image" testid="side-featured">
            <input
              value={post.featured_image_url || ""}
              onChange={(e) => setField({ featured_image_url: e.target.value })}
              placeholder="https://…"
              data-testid="editor-featured-image"
              className="w-full bg-white border border-black/15 rounded-md px-2 py-1.5 text-xs"
            />
            {post.featured_image_url && (
              <img src={post.featured_image_url} alt="" className="mt-1 rounded-md w-full h-auto object-cover" />
            )}
          </SidebarSection>
        </aside>
      </div>

      {/* PREVIEW modal (Blogger-style device chooser overlay) */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center" data-testid="preview-modal" onClick={() => setShowPreview(false)}>
          <div className="relative w-full h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Top ribbon: PREVIEW */}
            <div className="bg-[color:var(--np-brand,#B85F2A)] text-white text-center font-mono-tech text-[11px] tracking-[0.32em] uppercase py-1.5">Preview</div>
            {/* Device toolbar */}
            <div className="bg-white/95 border-b border-black/10 flex items-center gap-1 px-4 py-2 shadow">
              {[
                { m: "desktop", Icon: Desktop, label: "Desktop" },
                { m: "tablet", Icon: DeviceTablet, label: "Tablet" },
                { m: "mobile", Icon: DeviceMobile, label: "Mobile" },
              ].map(({ m, Icon, label }) => (
                <button
                  key={m}
                  onClick={() => setPreviewMode(m)}
                  data-testid={`preview-${m}`}
                  title={label}
                  className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] ${previewMode === m ? "bg-[#245BB0] text-white" : "text-[#241209] hover:bg-black/5"}`}
                >
                  <Icon size={13} weight="fill" /> {label}
                </button>
              ))}
              <button onClick={() => setPreviewRotated((v) => !v)} disabled={previewMode === "desktop"} title="Rotate" className="rounded-md px-2 py-1 text-[#241209] hover:bg-black/5 disabled:opacity-40" data-testid="preview-rotate"><ArrowsClockwise size={13} weight="fill" /></button>
              <button onClick={openPreviewInNewTab} title="Full preview in new tab" className="rounded-md px-2 py-1 text-[#241209] hover:bg-black/5" data-testid="preview-full"><ArrowsOut size={13} weight="fill" /></button>
              <div className="flex-1" />
              <button onClick={() => setShowPreview(false)} className="rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase" data-testid="preview-close">
                <X size={10} weight="bold" className="inline mr-1" /> Close
              </button>
            </div>
            {/* Preview frame */}
            <div className="flex-1 bg-[#333] flex items-center justify-center overflow-auto p-4">
              <div
                className="bg-[#F5EBDC] rounded-2xl shadow-2xl overflow-auto border border-black/10 transition-all"
                style={{ width: `${previewSize[0]}px`, maxWidth: "100%", height: `${previewSize[1]}px`, maxHeight: "calc(100vh - 200px)" }}
              >
                <div className="p-6 md:p-8">
                  <ListenTranslateBar getReadableText={() => `${post.title || ""}. ${post.subtitle || ""}. ${(post.body_html || "").replace(/<[^>]+>/g, " ")}`} />
                  {post.featured_image_url && <img src={post.featured_image_url} alt="" className="w-full rounded-lg mb-4" />}
                  <h1 className="font-serif-display text-4xl leading-tight">{post.title || "Untitled"}</h1>
                  {post.subtitle && <p className="mt-2 font-serif-display text-lg italic text-[#3A3830]">{post.subtitle}</p>}
                  <article className="prose prose-sm md:prose-base max-w-none mt-4" dangerouslySetInnerHTML={{ __html: post.body_html || "<p><em>Empty draft.</em></p>" }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SCHEDULE modal */}
      {showSchedule && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="schedule-modal" onClick={() => setShowSchedule(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Schedule publish</div>
            <h3 className="mt-1 font-serif-display text-xl">Pick a future date & time</h3>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              data-testid="schedule-datetime"
              className="mt-4 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowSchedule(false)} className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">Cancel</button>
              <button onClick={schedule} data-testid="schedule-confirm" className="rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">Schedule</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
